import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, BladApi, pobierzPlik } from '../api/client';
import { useLista } from '../api/lista';
import type {
  Indeks, KolumnaRaportu, Lista, Lokalizacja, OpisRaportu, Raport, Spis, TypKolumny,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import { Karta, Komunikat, Ladowanie, Pole, Przycisk, Pusto, Wybor } from '../components/ui';
import { data as fData, dataGodzina as fDataGodzina, ilosc as fIlosc, zlote } from '../lib/format';

/** Parametry, których żąda konkretne zestawienie. */
const PARAMETRY: Record<string, ('okres' | 'dni' | 'pozycja' | 'lokalizacja' | 'spis')[]> = {
  'lista-zakupowa': [],
  'terminy-waznosci': ['dni'],
  'przeglady': ['dni'],
  'zuzycie': ['okres'],
  'koszt-zajec': ['okres'],
  'majatek': ['lokalizacja'],
  'historia-pozycji': ['pozycja'],
  'protokol-roznic': ['spis'],
};

interface Filtry {
  od: string; do: string; dni: string;
  itemId: string; locationId: string; stocktakeId: string;
}

const PUSTE: Filtry = { od: '', do: '', dni: '', itemId: '', locationId: '', stocktakeId: '' };

function komorka(wartosc: unknown, typ: TypKolumny = 'tekst') {
  if (wartosc === null || wartosc === undefined || wartosc === '') return '—';
  switch (typ) {
    case 'kwota': return zlote(wartosc as number);
    case 'ilosc': return fIlosc(wartosc as number);
    case 'liczba': return String(wartosc);
    case 'data': return fData(wartosc as string);
    case 'dataGodzina': return fDataGodzina(wartosc as string);
    default: return String(wartosc);
  }
}

const doPrawej = (typ?: TypKolumny) => typ === 'kwota' || typ === 'ilosc' || typ === 'liczba';

export function Raporty() {
  const { maUprawnienie } = useAuth();
  const mozeEksportowac = maUprawnienie('raporty.eksport');
  const [kod, setKod] = useState('lista-zakupowa');
  const [filtry, setFiltry] = useState<Filtry>(PUSTE);
  const [bladPliku, setBladPliku] = useState<string | null>(null);
  const [pobieranie, setPobieranie] = useState<'xlsx' | 'pdf' | null>(null);

  const katalog = useQuery({
    queryKey: ['raporty', 'katalog'],
    queryFn: () => api.get<{ pozycje: OpisRaportu[] }>('/raporty'),
    staleTime: Infinity,
  });

  const potrzebne = PARAMETRY[kod] ?? [];
  const opis = katalog.data?.pozycje.find((p) => p.kod === kod);

  const zapytanie = useMemo(() => {
    const q = new URLSearchParams();
    if (potrzebne.includes('okres')) {
      if (filtry.od) q.set('od', filtry.od);
      if (filtry.do) q.set('do', filtry.do);
    }
    if (potrzebne.includes('dni') && filtry.dni) q.set('dni', filtry.dni);
    if (potrzebne.includes('pozycja') && filtry.itemId) q.set('itemId', filtry.itemId);
    if (potrzebne.includes('lokalizacja') && filtry.locationId) q.set('locationId', filtry.locationId);
    if (potrzebne.includes('spis') && filtry.stocktakeId) q.set('stocktakeId', filtry.stocktakeId);
    return q.toString();
  }, [kod, filtry, potrzebne]);

  // Raporty wymagające wskazania obiektu nie mają sensu bez niego — nie pytamy serwera na próżno.
  const brakujacy = opis?.wymaga === 'itemId' ? !filtry.itemId
    : opis?.wymaga === 'stocktakeId' ? !filtry.stocktakeId
    : false;

  const raport = useQuery({
    queryKey: ['raporty', kod, zapytanie],
    queryFn: () => api.get<Raport>(`/raporty/${kod}${zapytanie ? `?${zapytanie}` : ''}`),
    enabled: !brakujacy,
  });

  async function pobierz(format: 'xlsx' | 'pdf') {
    setBladPliku(null);
    setPobieranie(format);
    try {
      const q = new URLSearchParams(zapytanie);
      q.set('format', format);
      await pobierzPlik(`/raporty/${kod}?${q.toString()}`, `${kod}.${format}`);
    } catch (e) {
      setBladPliku(e instanceof BladApi ? e.message : 'Nie udało się pobrać pliku.');
    } finally {
      setPobieranie(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-kamien-900">Raporty</h1>
        <p className="mt-1 text-sm text-kamien-500">
          Zestawienia liczone na żywo z księgi ruchów. Każde można pobrać jako arkusz
          do dalszej obróbki albo jako PDF do podpisu.
        </p>
      </div>

      {katalog.isLoading ? <Ladowanie /> : (
        <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
          <nav aria-label="Wybór raportu" className="flex flex-col gap-1">
            {(katalog.data?.pozycje ?? []).map((p) => (
              <button
                key={p.kod}
                onClick={() => { setKod(p.kod); setBladPliku(null); }}
                aria-current={p.kod === kod ? 'true' : undefined}
                className={`rounded-md px-3 py-2.5 text-left transition-colors ${
                  p.kod === kod
                    ? 'bg-bordo-50 text-bordo-800 ring-1 ring-bordo-200'
                    : 'text-kamien-700 hover:bg-kamien-100'
                }`}
              >
                <span className="block text-sm font-medium">{p.nazwa}</span>
                <span className="mt-0.5 block text-xs text-kamien-500">{p.opis}</span>
              </button>
            ))}
          </nav>

          <div className="flex flex-col gap-4">
            {potrzebne.length > 0 && (
              <Karta>
                <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  {potrzebne.includes('okres') && (
                    <>
                      <Pole
                        etykieta="Od" type="date" value={filtry.od}
                        onChange={(e) => setFiltry({ ...filtry, od: e.target.value })}
                        podpowiedz="Pusto = ostatnie pół roku"
                      />
                      <Pole
                        etykieta="Do" type="date" value={filtry.do}
                        onChange={(e) => setFiltry({ ...filtry, do: e.target.value })}
                      />
                    </>
                  )}
                  {potrzebne.includes('dni') && (
                    <Pole
                      etykieta="Okno w dniach" type="number" min={1} max={1000}
                      value={filtry.dni} placeholder="90"
                      onChange={(e) => setFiltry({ ...filtry, dni: e.target.value })}
                      podpowiedz="Zaległe są zawsze uwzględnione"
                    />
                  )}
                  {potrzebne.includes('pozycja') && (
                    <WyborPozycji
                      wartosc={filtry.itemId}
                      zmien={(v) => setFiltry({ ...filtry, itemId: v })}
                    />
                  )}
                  {potrzebne.includes('lokalizacja') && (
                    <WyborLokalizacji
                      wartosc={filtry.locationId}
                      zmien={(v) => setFiltry({ ...filtry, locationId: v })}
                    />
                  )}
                  {potrzebne.includes('spis') && (
                    <WyborSpisu
                      wartosc={filtry.stocktakeId}
                      zmien={(v) => setFiltry({ ...filtry, stocktakeId: v })}
                    />
                  )}
                </div>
              </Karta>
            )}

            {bladPliku && <Komunikat rodzaj="blad">{bladPliku}</Komunikat>}

            {brakujacy ? (
              <Karta>
                <Pusto
                  tytul={opis?.wymaga === 'itemId' ? 'Wskaż pozycję kartoteki' : 'Wskaż inwentaryzację'}
                  opis="To zestawienie dotyczy jednego obiektu — wybierz go powyżej."
                />
              </Karta>
            ) : raport.isLoading ? <Ladowanie /> : raport.isError ? (
              <Komunikat rodzaj="blad">
                {raport.error instanceof BladApi ? raport.error.message : 'Nie udało się policzyć raportu.'}
              </Komunikat>
            ) : raport.data ? (
              <WidokRaportu
                raport={raport.data}
                mozeEksportowac={mozeEksportowac}
                pobieranie={pobieranie}
                pobierz={pobierz}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function WidokRaportu({ raport, mozeEksportowac, pobieranie, pobierz }: {
  raport: Raport;
  mozeEksportowac: boolean;
  pobieranie: 'xlsx' | 'pdf' | null;
  pobierz: (format: 'xlsx' | 'pdf') => void;
}) {
  return (
    <Karta
      tytul={raport.tytul}
      opis={raport.podtytul}
      akcje={mozeEksportowac ? (
        <div className="flex gap-2">
          <Przycisk onClick={() => pobierz('xlsx')} disabled={pobieranie !== null}>
            {pobieranie === 'xlsx' ? 'Pobieram...' : 'Arkusz XLSX'}
          </Przycisk>
          <Przycisk wariant="glowny" onClick={() => pobierz('pdf')} disabled={pobieranie !== null}>
            {pobieranie === 'pdf' ? 'Pobieram...' : 'PDF do podpisu'}
          </Przycisk>
        </div>
      ) : undefined}
    >
      {raport.kryteria && raport.kryteria.length > 0 && (
        <ul className="border-b border-kamien-200 px-5 py-3 text-xs text-kamien-500">
          {raport.kryteria.map((k) => <li key={k}>{k}</li>)}
        </ul>
      )}

      {raport.wiersze.length === 0 ? (
        <Pusto tytul="Brak danych" opis="Dla podanych kryteriów nic się nie zakwalifikowało." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-kamien-50 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                {raport.kolumny.map((k) => (
                  <th
                    key={k.klucz}
                    scope="col"
                    className={`whitespace-nowrap px-4 py-2.5 font-medium ${doPrawej(k.typ) ? 'text-right' : ''}`}
                  >
                    {k.naglowek}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-100">
              {raport.wiersze.map((w, i) => (
                <tr key={i} className="hover:bg-kamien-50">
                  {raport.kolumny.map((k) => (
                    <td
                      key={k.klucz}
                      className={`px-4 py-2 ${doPrawej(k.typ) ? 'text-right tabular-nums' : ''}`}
                    >
                      {komorka(w[k.klucz], k.typ)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {raport.podsumowanie && (
              <tfoot className="border-t-2 border-kamien-300 bg-kamien-50 font-medium">
                <tr>
                  {raport.kolumny.map((k: KolumnaRaportu) => (
                    <td
                      key={k.klucz}
                      className={`px-4 py-2.5 ${doPrawej(k.typ) ? 'text-right tabular-nums' : ''}`}
                    >
                      {raport.podsumowanie![k.klucz] === undefined
                        ? ''
                        : komorka(raport.podsumowanie![k.klucz], k.typ)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      <p className="border-t border-kamien-200 px-5 py-3 text-xs text-kamien-400">
        Policzono {fDataGodzina(raport.wygenerowano)} · wierszy: {raport.wiersze.length}
      </p>
    </Karta>
  );
}

function WyborPozycji({ wartosc, zmien }: { wartosc: string; zmien: (v: string) => void }) {
  const kartoteka = useLista({
    queryKey: ['kartoteka', 'do-raportu'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?naStronie=500'),
    staleTime: 60_000,
  });
  return (
    <Wybor etykieta="Pozycja kartoteki" value={wartosc} blad={kartoteka.blad}
      onChange={(e) => zmien(e.target.value)}>
      <option value="">— wybierz —</option>
      {kartoteka.pozycje.map((p) => (
        <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
      ))}
    </Wybor>
  );
}

function WyborLokalizacji({ wartosc, zmien }: { wartosc: string; zmien: (v: string) => void }) {
  const lokalizacje = useLista({
    queryKey: ['lokalizacje', 'do-raportu'],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>('/lokalizacje?tylkoAktywne=true'),
    staleTime: 60_000,
  });
  return (
    <Wybor etykieta="Lokalizacja" value={wartosc} blad={lokalizacje.blad}
      onChange={(e) => zmien(e.target.value)}>
      <option value="">Wszystkie</option>
      {lokalizacje.pozycje.map((l) => (
        <option key={l.id} value={l.id}>{l.code} · {l.name}</option>
      ))}
    </Wybor>
  );
}

function WyborSpisu({ wartosc, zmien }: { wartosc: string; zmien: (v: string) => void }) {
  const spisy = useLista({
    queryKey: ['inwentaryzacja', 'do-raportu'],
    queryFn: () => api.get<{ pozycje: Spis[] }>('/inwentaryzacja'),
    staleTime: 60_000,
  });
  return (
    <Wybor etykieta="Inwentaryzacja" value={wartosc} blad={spisy.blad}
      onChange={(e) => zmien(e.target.value)}>
      <option value="">— wybierz —</option>
      {spisy.pozycje.map((s) => (
        <option key={s.id} value={s.id}>{s.number} · {s.name}</option>
      ))}
    </Wybor>
  );
}
